// Dev-only tracking diagnostics HUD. Subscribes to RealXR8Driver's
// diagnostics store and renders a small numeric readout in the
// bottom-right corner so the developer can see what XR8 thinks of the
// scene (tracking status, scan progress vs threshold, camera motion).
// Helps locate SLAM-friendly surfaces in offices etc.
//
// Gated by import.meta.env.DEV in main.ts — production bundle excludes
// it entirely.

import { useCallback, useMemo, useSyncExternalStore } from 'react'
import type { TrackingDiagnostics } from '@/ar/real-xr8-driver'

interface DiagnosticsStoreLike {
  getState(): TrackingDiagnostics
  subscribe(listener: () => void): () => void
}

interface DiagnosticsHUDProps {
  store: DiagnosticsStoreLike
}

export function DiagnosticsHUD({ store }: DiagnosticsHUDProps) {
  const subscribe = useMemo(
    () => (cb: () => void) => store.subscribe(() => cb()),
    [store],
  )
  const getSnapshot = useCallback(() => store.getState(), [store])
  const d = useSyncExternalStore(subscribe, getSnapshot)

  const scanPct = d.normalFramesThreshold > 0
    ? Math.min(100, Math.round((d.normalFramesAccum / d.normalFramesThreshold) * 100))
    : 0

  // Movement guidance: too still (<0.5mm/frame) → SLAM can't triangulate;
  // too fast (>3cm/frame) → SLAM loses lock. Sweet spot is gentle pan.
  const speedMm = d.cameraSpeed * 1000
  const motionLabel =
    speedMm < 0.5 ? 'too still' : speedMm > 30 ? 'too fast' : 'good'

  return (
    <div
      data-testid="diagnostics-hud"
      style={{
        position: 'fixed',
        bottom: 6,
        right: 6,
        padding: '6px 10px',
        background: 'rgba(0, 0, 0, 0.75)',
        color: 'white',
        font: '11px/1.4 ui-monospace, "SF Mono", Menlo, monospace',
        borderRadius: 8,
        pointerEvents: 'none',
        zIndex: 100,
        minWidth: 160,
      }}
    >
      <Row label="status" value={d.trackingStatus} accent={statusAccent(d.trackingStatus)} />
      {d.trackingReason != null && (
        <Row label="reason" value={d.trackingReason} dim />
      )}
      <Row
        label="scan"
        value={`${d.normalFramesAccum}/${d.normalFramesThreshold} (${scanPct}%)`}
        accent={scanPct >= 100 ? '#73ff8b' : scanPct >= 50 ? '#ffe27a' : undefined}
      />
      <Row
        label="motion"
        value={`${speedMm.toFixed(1)}mm/f ${motionLabel}`}
        accent={motionLabel === 'good' ? '#73ff8b' : '#ffb6b6'}
      />
      <Row
        label="cam"
        value={`(${fmt(d.cameraPosition.x)}, ${fmt(d.cameraPosition.y)}, ${fmt(d.cameraPosition.z)})`}
        dim
      />
      <Row label="frames" value={String(d.totalFrames)} dim />
      {d.eventKeys.length > 0 && (
        <Row label="event" value={d.eventKeys} dim />
      )}
      {d.cameraDetailsKeys.length > 0 && (
        <Row label="cd-keys" value={d.cameraDetailsKeys} dim />
      )}
    </div>
  )
}

function Row({
  label,
  value,
  accent,
  dim,
}: {
  label: string
  value: string
  accent?: string
  dim?: boolean
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ opacity: 0.6 }}>{label}</span>
      <span style={{ color: accent, opacity: dim ? 0.7 : 1 }}>{value}</span>
    </div>
  )
}

function statusAccent(status: string): string {
  if (status === 'NORMAL') return '#73ff8b'
  if (status === 'LIMITED') return '#ffb6b6'
  return '#ffe27a' // INITIALIZING + anything else
}

function fmt(n: number): string {
  return n.toFixed(2)
}
